import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Alert,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useTranslations } from "use-intl";
import { getAccessibleTenantList, Tenant } from "../services/api";
import { TenantListFilter } from "../services/api";
import useAuth from "../../../hooks/useAuth";
import { useTenantSelection } from "../contexts/TenantSelection";

const STORAGE_KEY_STARRED_TENANTS = "starredTenants";

const TenantSelector: React.FC = () => {
  const t = useTranslations("tenantSelection");
  const { close, isOpen, prefillTenantId } = useTenantSelection();
  const {  updateUserTenant, currentUser } = useAuth();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [starredTenants, setStarredTenants] = useState<Tenant[]>([]);
  const [starredTenantIds, setStarredTenantIds] = useState<Set<number>>(
    new Set()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantIdSearch, setTenantIdSearch] = useState("");
  const [debouncedTenantSearch, setDebouncedTenantSearch] = useState("");
  const [debouncedTenantIdSearch, setDebouncedTenantIdSearch] = useState("");

  const isSuperAdmin = currentUser?._isSuperAdmin === true;

  // Load starred tenants only for super admins
  useEffect(() => {
    if (!isSuperAdmin) {
      setStarredTenants([]);
      setStarredTenantIds(new Set());
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY_STARRED_TENANTS);
      if (stored) {
        const starred: Tenant[] = JSON.parse(stored);
        setStarredTenants(starred);
        setStarredTenantIds(new Set(starred.map((t) => t.id)));
      }
    } catch (err) {
      console.error("Failed to load starred tenants:", err);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTenantSearch(tenantSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [tenantSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTenantIdSearch(tenantIdSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [tenantIdSearch]);

  useEffect(() => {
    if (isOpen && prefillTenantId) {
      setTenantIdSearch(prefillTenantId);
    }
  }, [isOpen, prefillTenantId]);

  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);
      setError(null);
      try {
        const filters: TenantListFilter = {
          tenant: debouncedTenantSearch || undefined,
          tenantId: debouncedTenantIdSearch
            ? parseInt(debouncedTenantIdSearch)
            : undefined,
          limit: 10,
        };
        const data = await getAccessibleTenantList(filters);
        setTenants(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("fetchFailed")
        );
        setTenants([]);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchTenants();
    }
  }, [isOpen, debouncedTenantSearch, debouncedTenantIdSearch, t]);

  const handleTenantSelect = async (tenant: Tenant) => {
    if (switching) return;

    setSwitching(true);
    try {
      await updateUserTenant(tenant.id);
      close();
    } catch (error) {
      // Error already handled in updateUserTenant with toast
      console.error("Tenant selection failed:", error);
    } finally {
      setSwitching(false);
    }
  };

  const handleStarToggle = (tenant: Tenant, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!isSuperAdmin) return;

    const newStarredTenantIds = new Set(starredTenantIds);
    let newStarredTenants = [...starredTenants];

    if (starredTenantIds.has(tenant.id)) {
      newStarredTenantIds.delete(tenant.id);
      newStarredTenants = newStarredTenants.filter((t) => t.id !== tenant.id);
    } else {
      newStarredTenantIds.add(tenant.id);
      newStarredTenants.push(tenant);
    }

    setStarredTenantIds(newStarredTenantIds);
    setStarredTenants(newStarredTenants);

    try {
      localStorage.setItem(
        STORAGE_KEY_STARRED_TENANTS,
        JSON.stringify(newStarredTenants)
      );
    } catch (err) {
      console.error("Failed to save starred tenants:", err);
    }
  };

  const handleClearFilters = () => {
    setTenantSearch("");
    setTenantIdSearch("");
  };

  const hasTenantFilters = tenantSearch || tenantIdSearch;

  const renderTenantList = (tenantList: Tenant[], title?: string) => {
    if (tenantList.length === 0) return null;

    return (
      <Box>
        {title && (
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        <List
          sx={{
            maxHeight: 250,
            overflow: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          {tenantList.map((tenant) => (
            <ListItemButton
              key={tenant.id}
              selected={currentUser?.tenantId === tenant.id}
              onClick={() => handleTenantSelect(tenant)}
              disabled={switching}
            >
              <ListItemText
                primary={tenant.esKey}
                secondary={
                  tenant.zohoCustomerName
                    ? t("tenantIdWithCustomer", { id: String(tenant.id), customer: tenant.zohoCustomerName })
                    : t("tenantIdLabel", { id: String(tenant.id) })
                }
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {currentUser?.tenantId === tenant.id && (
                  <CheckCircleIcon color="success" />
                )}
                {isSuperAdmin && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleStarToggle(tenant, e)}
                    sx={{ ml: 1 }}
                  >
                    {starredTenantIds.has(tenant.id) ? (
                      <StarIcon sx={{ color: "gold" }} />
                    ) : (
                      <StarBorderIcon />
                    )}
                  </IconButton>
                )}
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t("title")}
          <Tooltip title={t("close")}>
            <IconButton size="small" onClick={close} disabled={switching}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {isSuperAdmin && starredTenants.length > 0 && !hasTenantFilters && (
            <>
              {renderTenantList(starredTenants, t("starredTenants"))}
              <Divider />
            </>
          )}

          <Box>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("tenantName")}
                    placeholder={t("searchByName")}
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    size="small"
                    disabled={switching}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("tenantId")}
                    placeholder={t("searchById")}
                    value={tenantIdSearch}
                    onChange={(e) => setTenantIdSearch(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0 }}
                    disabled={switching}
                  />
                </Grid>
              </Grid>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading && (
              <Stack spacing={1}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={50} />
                ))}
              </Stack>
            )}

            {!loading && tenants.length === 0 && !error && (
              <Alert severity="info">
                {hasTenantFilters
                  ? t("noMatches")
                  : t("enterCriteria")}
              </Alert>
            )}

            {!loading && tenants.length > 0 && renderTenantList(tenants)}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClearFilters}
          variant="outlined"
          color="secondary"
          disabled={!hasTenantFilters || switching}
        >
          {t("clearFilters")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantSelector;